import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldValues, Path, Resolver, UseFormSetError } from 'react-hook-form';
import type { z } from 'zod';
import { isApiError } from './api';

/**
 * Resolver Zod usando os schemas do contrato. `prepare` normaliza o que o formulário
 * guarda (ex.: "" vira null) antes da validação, sem duplicar regras no front.
 */
export function contractResolver<S extends z.ZodType, TIn extends FieldValues = z.input<S> & FieldValues>(
  schema: S,
  prepare?: (values: TIn) => unknown,
): Resolver<TIn, unknown, z.output<S>> {
  const base = zodResolver(schema as never) as unknown as Resolver<TIn, unknown, z.output<S>>;
  return (values, ctx, opts) => base(prepare ? (prepare(values) as TIn) : values, ctx, opts);
}

/** "" vira null nos campos indicados (campos opcionais e anuláveis do contrato). */
export function emptyToNull<T extends object>(values: T, keys: (keyof T)[]): T {
  const out = { ...values };
  for (const k of keys) if ((out[k] as unknown) === '' || (out[k] as unknown) === undefined) out[k] = null as T[keyof T];
  return out;
}

/** Aplica os `issues` devolvidos pela API nos campos do formulário; devolve a mensagem geral, se houver. */
export function applyServerErrors<T extends FieldValues>(error: unknown, setError: UseFormSetError<T>): string | null {
  if (!isApiError(error)) return 'Algo deu errado. Tente novamente.';
  let matched = 0;
  for (const issue of error.issues) {
    if (!issue.path) continue;
    setError(issue.path as Path<T>, { type: 'server', message: issue.message });
    matched++;
  }
  return matched > 0 ? null : error.message;
}
